import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import GameHeader from '../GameHeader';
import StatusRow from '../StatusRow';
import MainPanel from './MainPanel';
import TasksPanel from './TasksPanel';
import CodesPanel from './CodesPanel';
import TemplatesPanel from './TemplatesPanel';
import ParticipantsPanel from './participants/ParticipantsPanel';
import '../games.css';
import './spite.css';

const SPITE_STATUSES = [
    { value: 'DEVELOP', label: 'Подготовка', tone: 'dev' },
    { value: 'ACTIVE', label: 'Активна', tone: 'active' },
    { value: 'FINISHED', label: 'Завершена', tone: 'done' }
];

const TABS = [
    { key: 'main', label: 'Основная' },
    { key: 'tasks', label: 'Задания' },
    { key: 'codes', label: 'Коды' },
    { key: 'tmpl', label: 'Шаблоны' },
    { key: 'players', label: 'Участники' }
];

// Игра «Насоли соседу»: head-card (инфо + статус) + табы основная/задания/коды/шаблоны.
export default function SpiteGame({ shift }) {
    const smenaId = shift.id;
    const { data: neighbor, isLoading, error } = useApiQuery(
        ['neighbor', 'main', smenaId],
        `/api/neighbor/admin/neighbors/find-by-smena?smenaId=${smenaId}`,
        { enabled: !!smenaId, refetchOnMount: 'always' }
    );

    if (error) return <div className="vp-empty">{error.response?.data?.message || 'Ошибка загрузки игры «Насоли соседу»'}</div>;
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!neighbor) return <div className="vp-empty">Игра «Насоли соседу» не найдена для смены</div>;

    return <SpiteContent neighbor={neighbor} shift={shift} />;
}

SpiteGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function SpiteContent({ neighbor, shift }) {
    const smenaId = shift.id;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [tab, setTab] = useState('main');
    const [status, setStatus] = useState(neighbor.status);

    const { data: categories = [] } = useApiQuery(
        ['neighbor', 'task-categories', neighbor.id],
        `/api/neighbor/admin/task-categories/list-by-neighbor?neighborId=${neighbor.id}`,
        { refetchOnMount: 'always' }
    );
    const { data: tasks = [] } = useApiQuery(
        ['neighbor', 'tasks', neighbor.id],
        `/api/neighbor/admin/tasks/list-by-neighbor?neighborId=${neighbor.id}`,
        { refetchOnMount: 'always' }
    );

    const changeStatus = async (newStatus) => {
        show();
        try {
            await apiClient.patch(`/api/neighbor/admin/neighbors/update?id=${neighbor.id}`, { status: newStatus });
            setStatus(newStatus);
            enqueueSnackbar('Статус обновлён', { variant: 'success' });
        } catch (e) { enqueueSnackbar('Ошибка при обновлении статуса', { variant: 'error' }); }
        finally { hide(); }
    };

    const meta = (
        <>
            <span><b>{categories.length}</b> категорий</span>
            <span className="dot">·</span>
            <span><b>{tasks.length}</b> заданий</span>
        </>
    );

    return (
        <>
            <GameHeader
                title={`Насоли соседу — ${shift.title || ''}`}
                meta={meta}
                status={<StatusRow statuses={SPITE_STATUSES} current={status} onChange={changeStatus} />}
            />
            <section className="content white">
                <div className="bt-tabs">
                    {TABS.map(t => (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.label}</a>
                    ))}
                </div>
                {tab === 'main' && <MainPanel neighbor={neighbor} />}
                {tab === 'tasks' && <TasksPanel neighbor={neighbor} smenaId={smenaId} />}
                {tab === 'codes' && <CodesPanel neighbor={neighbor} smenaId={smenaId} />}
                {tab === 'tmpl' && <TemplatesPanel />}
                {tab === 'players' && <ParticipantsPanel smenaId={smenaId} qrBaseUrl={neighbor.qrBaseUrl} />}
            </section>
        </>
    );
}

SpiteContent.propTypes = {
    neighbor: PropTypes.object.isRequired,
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};
